import React, { useCallback, useEffect, useRef, useState } from 'react'
import Icon from './icons/Icon'

/** How long the confirmation label stays up before reverting to "Copy". */
const FEEDBACK_MS = 2000

type CopyState = 'idle' | 'copied' | 'failed'

/**
 * Copy `text` to the clipboard, preferring the async Clipboard API.
 *
 * `navigator.clipboard` only exists in a secure context, so it is absent when
 * the dev server is reached over plain http from another device on the LAN.
 * The textarea fallback is deprecated but still the only thing that works
 * there, and a copy button that silently does nothing is worse than a
 * deprecation.
 */
const writeToClipboard = async (text: string): Promise<boolean> => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch {
      // Permission denied or a non-focused document — fall through.
    }
  }

  try {
    const scratch = document.createElement('textarea')
    scratch.value = text
    scratch.setAttribute('readonly', '')
    scratch.style.position = 'fixed'
    scratch.style.opacity = '0'
    scratch.style.pointerEvents = 'none'
    document.body.appendChild(scratch)
    scratch.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(scratch)
    return ok
  } catch (error) {
    console.error('Clipboard copy failed', {
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      context: { length: text.length },
    })
    return false
  }
}

export interface CopyButtonProps {
  /** The exact text placed on the clipboard. */
  value: string
  /** Accessible name, when several copy buttons share a view. */
  label?: string
  className?: string
}

/**
 * Small copy-to-clipboard control.
 *
 * Copies the message's raw Markdown rather than the rendered DOM, so what
 * lands on the clipboard is the source Claude produced — tables and code
 * fences survive a paste into another editor instead of collapsing to
 * whitespace.
 */
const CopyButton: React.FC<CopyButtonProps> = ({ value, label = 'Copy message', className }) => {
  const [state, setState] = useState<CopyState>('idle')
  const resetTimer = useRef<number | null>(null)

  // The timer outlives a fast unmount (switching conversations right after a
  // copy), so clear it rather than setting state on a gone component.
  useEffect(() => {
    return () => {
      if (resetTimer.current !== null) window.clearTimeout(resetTimer.current)
    }
  }, [])

  const handleCopy = useCallback(async () => {
    const ok = await writeToClipboard(value)
    setState(ok ? 'copied' : 'failed')
    if (resetTimer.current !== null) window.clearTimeout(resetTimer.current)
    resetTimer.current = window.setTimeout(() => setState('idle'), FEEDBACK_MS)
  }, [value])

  const text = state === 'copied' ? 'Copied' : state === 'failed' ? 'Copy failed' : 'Copy'

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={`copy-button${className ? ` ${className}` : ''}`}
      data-state={state}
      aria-label={label}
    >
      <Icon name={state === 'copied' ? 'check' : 'copy'} />
      <span aria-live="polite">{text}</span>
    </button>
  )
}

export default CopyButton
